import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { RootState } from 'store';

import { hotelsFilterClear, hotelsSearchRequest } from 'action-creators/hotels';
import { accountFailure, accountRequest } from 'action-creators/user';
import { hotelResults, searchHotels, getFilters } from 'actions/hotels';
import { updateAccount } from 'actions/user';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { HotelFilterResponse } from 'models/hotelFilter';
import HotelsList from 'screens/components/HotelsList';
import { amplitudelogEvent, ViewTravelResultsScreenEvent } from 'services/amplitudeService';
import { getAmenities, HotelAmenities } from 'services/hotelService';
import { fetchAccount } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage } from 'utils/generic';

const HotelsListContainer = () => {
  const toast = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();

  const { accountResult } = useSelector((state: RootState) => state.user);
  const {
    destinationPoint,
    checkInDate,
    checkOutDate,
    traveler,
    search,
    isLoadingResults,
    branchCompany,
  } = useSelector((state: RootState) => state.hotels);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [amenitiesCodes, setAmenitiesCodes] = useState<HotelAmenities[]>([]);
  const [availableFilters, setAvailableFilters] = useState<HotelFilterResponse>();
  const user = useSelector((state: RootState) => state.user.accountResult);

  const handleHotelSearch = useCallback(async () => {
    if (destinationPoint && checkInDate && checkOutDate) {
      dispatch(accountRequest());
      const {
        data: { data },
      } = await fetchAccount();

      if (data.sessionToken === '' || data.sessionToken === undefined || data.sessionToken === null) {
        dispatch(accountFailure('sessionToken is empty'));
        history.replace('/login');
      }

      updateAccount(dispatch, data);
      const payload = {
        destinationPoint,
        checkInDate,
        checkOutDate,
        traveler,
        newSessionToken: data.sessionToken,
        branchCompany: branchCompany,
      };
      dispatch(hotelsFilterClear());
      dispatch(hotelsSearchRequest(payload));
      return await dispatch(searchHotels(payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, destinationPoint, dispatch, traveler, branchCompany]);

  const handleHotelFilter = useCallback(async () => {
    try {
      const data: HotelFilterResponse = await dispatch<any>(getFilters());
      setAvailableFilters(data);
    } catch (e) {
      toast.add({ type: 'danger', message: t('filters.fetchError') });
    }
  }, [dispatch, t, toast]);

  const handleHotelAmenities = useCallback(async () => {
    try {
      if (!accountResult) return;
      setAmenitiesCodes(await getAmenities(accountResult.sessionToken, t('locale')));
    } catch (e) {
      toast.add({ type: 'danger', message: t('filters.fetchError') });
    }
  }, [accountResult, t, toast]);
  const handleHotelResults = useCallback(async () => await dispatch(hotelResults()), [dispatch]);

  useEffect(() => {
    const handleSearch = async () => {
      const now = new Date();
      const before = search?.resultsAvailableUntil ? new Date(search?.resultsAvailableUntil) : null;
      const hasExpired = before ? before < now : true;

      if (
        (hasExpired || search === null || search === undefined) &&
        (search?.results === null || search?.results === undefined || search?.results.length === 0) &&
        !isLoadingResults &&
        !loadingSearch
      ) {
        try {
          setLoadingSearch(true);
          const res: any = await handleHotelSearch();

          if (res.resultsCount === 0 && isMobile) {
            history.replace('/booking/hotels', { showNoResults: true });
            return;
          }

          if (res.resultsCount > 0) {
            //Amplitude
            const event: ViewTravelResultsScreenEvent = {
              SOURCE: 'TRAVEL_HOME',
              RESULTS: res?.resultsCount ?? 0,
              TRAVEL_PRODUCT_TYPE: 'HOTELS',
              TRAVEL_FROM: '',
              TRAVEL_TO: destinationPoint?.id.toString() ?? '',
              TRAVEL_DESTINATION_TYPE: '',
              TRAVEL_TRIP_TYPE: '',
              TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
              TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
              TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
              TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
              TRAVEL_PASSENGERS: '1',
              TRAVEL_ADULTS: '1',
              TRAVEL_CHILDREN: '0',
              TRAVEL_INFANTS: '0',
              TRAVEL_CLASS_OF_SERVICE: '',
              TRAVEL_ROOMS: '1',
              TRAVEL_WITH_BAGGAGE: '',
              TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
              SEARCHID: search?.searchId ?? '',
            };

            amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_RESULTS_SCREEN, event, user);

            await Promise.all([handleHotelResults(), handleHotelAmenities(), handleHotelFilter()]);
          }
        } catch (error) {
          const message = extractErrorMessage(error);

          if (message?.includes('No results available for current search')) {
            if (isMobile) history.replace('/booking/hotels', { showNoResults: true });
            return;
          }

          if (message?.startsWith('Currency reported is different')) {
            Sentry.captureMessage('[Hotels] Currency reported is different');
            toast.add({ type: 'danger', message: t('hotelsList.error.differentCurrency') });
            history.replace('/booking/hotels');
            return;
          }

          toast.add({ type: 'danger', message: message || t('defaultError.default') });
          history.replace('/booking/hotels', { showNoResults: true });
        } finally {
          setLoadingSearch(false);
        }
      }
    };

    if (destinationPoint && checkInDate && checkOutDate) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, destinationPoint, traveler, search]);

  const handleRequest = useCallback(() => dispatch(hotelResults()), [dispatch]);

  return (
    <HotelsList
      destinationPoint={destinationPoint}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      isLoading={loadingSearch}
      isLoadingResults={isLoadingResults}
      hotelsResult={search?.results.filter((result) => result.currencyCode) || []}
      resultsCount={search?.resultsCount}
      onRequest={handleRequest}
      availableFilters={availableFilters}
      amenitiesCodes={amenitiesCodes}
      traveler={traveler}
    />
  );
};

export default HotelsListContainer;
